import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";
import { auth } from "../../firebase";
import companiesQuery from "../../firestore/Companies.query";
import { MovieCategoriesDeleteModal } from "./MovieCategoriesDeleteModal";
import { MovieCategoriesModal } from "./MovieCategoriesModal";
import { newVideoCategorys, VideoCategorys } from "../../model/VideoCategorys";
import DraggableTable, { GridCol as DraggableGridCol, GridRow as DraggableGridRow } from "../../components/table/DraggableTable";
import { GridCol, GridRow } from "../../components/table/TableList";
import { convertDateToString } from "../../utility/ConvertUtily";
import { VideoContents } from "../../model/VideoContents";
import { MovieContentsSelectModal } from "./MovieContentsSelectModal";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { MovieContentsAddModal } from "./MovieContentsAddModal";
import { MovieContentsModal } from "./MovieContentsModal";
import { MovieContentsDeleteModal } from "./MovieContentsDeleteModal";
import { ConstMessage, CreateUserName, CreateUserType } from "../../config/const";
import { isOptionActive } from "../../controller/OptionController";
import { Staff } from "../../model/Staff";

export const MovieMenuSettingPage = () => {
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const isDisabled = useRef(false);

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isSelectContentsOpen, setIsSelectContentsOpen] = useState(false);
  const [isContentsOpen, setIsContentsOpen] = useState(false);
  const [isContentsAddOpen, setIsContentsAddOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isContentsDialogOpen, setIsContentsDialogOpen] = useState(false);

  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [isDraggable, setIsDraggable] = useState(true);

  const [videoCategorys, setVideoCategorys] = useState<VideoCategorys>(newVideoCategorys);
  const [deleteVideoCategorys, setDeleteVideoCategorys] = useState<VideoCategorys | undefined>();
  const [deleteVideoContents, setDeleteVideoContents] = useState<VideoContents | undefined>();

  const [videoContentsList, setVideoContentsList] = useState<VideoContents[]>([]);
  const [rowList, setRowList] = useState<DraggableGridRow[]>([]);
  const [genreRowList, setGenreRowList] = useState<DraggableGridRow[]>([]);
  const [contentsRowList, setContentsRowList] = useState<GridRow[]>([]);
  const [selectContentsIdList, setSelectContentsIdList] = useState<string[]>();
  const [contentsSelectDialogSelectedIdList, setContentsSelectDialogSelectedIdList] = useState<string[]>();
  const [contentsData, setContentsData] = useState<VideoContents>();

  const [staffList, setStaffList] = useState<Staff[]>([]);

  const [optionFlg, setOptionFlg] = useState<{ point: boolean }>({
    point: false,
  });
  const [submitFlg, setSubmitFlg] = useState(false);

  // オプション設定出し分け
  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;
    return { point };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  useEffect(() => {
    const f = async () => {
      try {
        setIsLoad(true);
        // ジャンル一覧取得
        const videoCategoriesList = await companiesQuery.getVideoCategoriesList(
          currentUser?.tenantId!);

        // スタッフ一覧取得
        const staffs = await companiesQuery.getStaffList(currentUser?.tenantId!);
        setStaffList(staffs);

        const rowList: DraggableGridRow[] = videoCategoriesList.map((videoCategorys, index) => {
          return {
            rowData: {
              id: videoCategorys.id,
              number: videoCategorys.displayOrder,
              title: (<div className={"sm:whitespace-normal"}>{videoCategorys.title ?? ''}</div>),
              type: getRegisterName(videoCategorys.type, videoCategorys.createUser, staffs),
              createUser: videoCategorys.createUser,
              control: videoCategorys.type === "admin" ? (<></>) : (
                <div className="flex space-x-2">
                  <Button
                    className="whitespace-nowrap"
                    onClick={() => onEditGenre(videoCategorys, videoContentsList)}
                    children={"編集"}/>
                  <Button
                    color="red"
                    onClick={() => {
                      deleteDialogToggle();
                      setDeleteVideoCategorys(videoCategorys);
                    }}
                    className="whitespace-nowrap"
                    children={"削除"}/>
                </div>
              )
            },
            rawRowData: {
              id: videoCategorys.id,
              type: videoCategorys.type === "admin" ? "運営" : "団体",
              title: videoCategorys.title ?? '',
              createUser: videoCategorys.createUser
            }
          };
        });
        rowList.sort((a, b) => parseInt(a.rowData.number.toString()) - parseInt(b.rowData.number.toString()));
        setRowList(rowList);

        // コンテンツ一覧取得
        const videoContentsList = await companiesQuery.getVideoContentsList(
          currentUser?.tenantId!);
        setVideoContentsList(videoContentsList);

        const contentsRowList: GridRow[] = videoContentsList.map((contents) => {
          return {
            rowData: {
              select: (
                <input
                  id={contents.id}
                  name={contents.id}
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  data-id={contents.id}
                  onChange={onSelectContents}
                  checked={contentsSelectDialogSelectedIdList?.includes(contents.id)}
                />
              ),
              title: (<div className={'truncate w-56'}>{contents.title}</div>),
              type: contents.type,
              author: getRegisterName(contents.createUserType!, contents.createUser, staffs),
              explanation: (<div className={'truncate w-56'}>{contents.explanation}</div>),
              createAt: convertDateToString(contents.createAt),
              actions: (contents.createUserType === 'municipality') ? (
                <div className={'flex space-x-1'}>
                  <Button
                    color={'indigo'}
                    size={"sm"}
                    className={'sm:p-1 whitespace-nowrap'}
                    children={"編集"}
                    onClick={() => onContentsEdit(contents)}
                  />
                  <Button
                    color={'red'}
                    size={"sm"}
                    className={'sm:p-1 whitespace-nowrap'}
                    children={"削除"}
                    onClick={() => {
                      deleteContentsDialogToggle();
                      setDeleteVideoContents(contents);
                    }}
                  />
                </div>
              ) : (<></>),
              point: contents.pointSettingFlg ? (
                <div className="text-right pr-2">
                  {contents.pointAssignmentPoint}
                </div>
              ) : null,
            },
            sortableRowData: {
              select: contentsSelectDialogSelectedIdList?.includes(contents.id) ?? false,
              title: contents.title,
              explanation: contents.explanation,
              actions: contents.createUserType === 'municipality',
              point: contents.pointAssignmentPoint ?? 0,
            },
            style: 'sm:whitespace-normal'
          }
        });
        setContentsRowList(contentsRowList);

      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));

      } finally {
        setIsLoad(false);
      }
    };
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitFlg, isDraggable]);

  useEffect(() => {
    const contentsRowList: GridRow[] = videoContentsList.map((contents) => {
      return {
        rowData: {
          select: (
            <input
              id={contents.id}
              name={contents.id}
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              data-id={contents.id}
              onChange={onSelectContents}
              checked={contentsSelectDialogSelectedIdList?.includes(contents.id)}
            />
          ),
          title: (<div className={'truncate w-56'}>{contents.title}</div>),
          type: contents.type,
          author: getRegisterName(contents.createUserType!, contents.createUser, staffList),
          explanation: (<div className={'truncate w-56'}>{contents.explanation}</div>),
          createAt: convertDateToString(contents.createAt),
          actions: (contents.createUserType === 'municipality') ? (
            <div className={'flex space-x-1'}>
              <Button
                color={'indigo'}
                size={"sm"}
                className={'sm:p-1 whitespace-nowrap'}
                children={"編集"}
                onClick={() => onContentsEdit(contents)}
              />
              <Button
                color={'red'}
                size={"sm"}
                className={'sm:p-1 whitespace-nowrap'}
                children={"削除"}
                onClick={() => {
                  deleteContentsDialogToggle();
                  setDeleteVideoContents(contents);
                }}
              />
            </div>
          ) : (<></>),
          point: contents.pointSettingFlg ? (
            <div className="text-right pr-2">
              {contents.pointAssignmentPoint}
            </div>
          ) : null,
        },
        sortableRowData: {
          select: contentsSelectDialogSelectedIdList?.includes(contents.id) ?? false,
          title: contents.title,
          explanation: contents.explanation,
          actions: contents.createUserType === 'municipality',
          point: contents.pointAssignmentPoint ?? 0,
        },
        style: 'sm:whitespace-normal'
      }
    });
    setContentsRowList(contentsRowList);
  }, [contentsSelectDialogSelectedIdList]);

  /**
   * 作成者を返却する
   * 
   * @param createUserType 
   * @param createUserMail 
   * @param staffList 
   * @returns 運営で作成：運営 / 団体で作成：作成者名（削除済の管理者の場合：団体）
   */
  const getRegisterName = (createUserType: string, createUserMail:string, staffList: Staff[]) => {
    if (createUserType === CreateUserType.ADMIN) {
      return CreateUserName.ADMIN;
    }
    // メールがない場合は団体
    if (!createUserMail) {
      return CreateUserName.MUNICIPALITY;
    }
    // メールでスタッフを検索
    const matchedStaff = staffList.find((staff) => staff.email === createUserMail);
    if (matchedStaff?.displayName) {
      return matchedStaff.displayName;
    }
    // 削除済or不明
    return CreateUserName.MUNICIPALITY;
  };

  const columns: DraggableGridCol[] = [
    {
      field: "title",
      headerName: "ジャンル名",
      style: "",
    },
    {
      field: "type",
      headerName: "作成者",
      style: "",
    },
    {
      field: "control",
      headerName: "編集/削除",
      style: "w-2/12",
    },
  ];

  const genreColumns: DraggableGridCol[] = [
    {
      field: "title",
      headerName: "動画名",
      style: "",
    },
    {
      field: "type",
      headerName: "動画タイプ",
      style: "",
    },
    {
      field: "author",
      headerName: "登録者",
      style: "",
    },
    {
      field: "createAt",
      headerName: "登録日",
      style: "",
    },
  ];

  const contentsColumns = useMemo((): GridCol[] => {
    const baseCols: GridCol[] = [
      {
        field: "select",
        headerName: "",
        style: "w-1/12",
      },
      {
        field: "title",
        headerName: "動画名",
        style: "w-56",
      },
      {
        field: "type",
        headerName: "動画タイプ",
        style: "w-1/12",
      },
      {
        field: "explanation",
        headerName: "説明",
        style: "w-64",
      },
      {
        field: "author",
        headerName: "登録者",
        style: "w-2/12",
      },
      {
        field: "createAt",
        headerName: "登録日",
        style: "w-1/12",
      },
    ];
    // ポイント管理が有効の場合
    if (optionFlg.point) {
      baseCols.splice(4, 0, {
        field: "point",
        headerName: "ポイント数",
        style: "w-1/12",
      });
    }
  
    return baseCols;
  }, [optionFlg]);
  
  const contentsListColumns = useMemo((): GridCol[] => {
    const baseCols: GridCol[] = [
      {
        field: "title",
        headerName: "動画名",
        style: "w-56",
      },
      {
        field: "type",
        headerName: "動画タイプ",
        style: "w-1/12",
      },
      {
        field: "explanation",
        headerName: "説明",
        style: "w-64",
      },
      {
        field: "author",
        headerName: "登録者",
        style: "w-2/12",
      },
      {
        field: "createAt",
        headerName: "登録日",
        style: "w-1/12",
      },
    ];
    // ポイント管理が有効の場合
    if (optionFlg.point) {
      baseCols.splice(3, 0, {
        field: "point",
        headerName: "ポイント数",
        style: "w-1/12",
      });
    }
  
    baseCols.push({
      field: "actions",
      headerName: "",
      style: "w-1/12",
    });
  
    return baseCols;
  }, [optionFlg]);

  // 動画メニュー削除ダイアログ開閉
  const deleteDialogToggle = () => {
    setIsDialogOpen(!isDialogOpen);
  };
  // 動画メニュー削除
  const onDelete = async (videoCategoryId: string) => {
    setIsLoad(true);
    await companiesQuery.deleteVideoCategory(currentUser?.tenantId!, videoCategoryId);
    dispatch(alertOpen(AlertValue("動画メニューから削除しました。", "success")));
    deleteDialogToggle();
    setSubmitFlg(!submitFlg);
  };
  // 動画メニュー並べ替え
  const draggable = async (rowDataList: any) => {
    setIsLoad(true);
    try{
      const updateVideoCategorys = rowDataList.map((data: any, index: number): VideoCategorys => {
        const rowData = data.rawRowData;
        return {
          id: rowData.id,
          displayOrder: (index + 1).toString(),
          type: rowData.type === '団体' ? 'municipality' : 'admin',
          title: rowData.title,
          createUser: rowData.createUser,
          createAt: new Date(),
        }
      });
      const resolve = await companiesQuery.upsertVideoCategories(currentUser?.tenantId!, updateVideoCategorys);
      if(resolve.length > 0){
        setIsDraggable(!isDraggable);
      }
    } catch (err: any) {
      console.error(err)
      setIsLoad(false);
    }
  };

  // ジャンル登録モーダル開閉
  const toggle = () => {
    setIsOpen(prev => !prev);
  }
  // ジャンル登録並べ替え
  const genreDraggable = async (rowData: DraggableGridRow[]) => {
    const newGenreRowList = rowData.map((genre, index) => {
      return {
        rowData: {
          id: genre.rowData.id,
          author: genre.rowData.author,
          number: (index + 1).toString(),
          createAt: genre.rowData.createAt,
          title: genre.rowData.title,
          type: genre.rowData.type
        }
      }
    });
    setGenreRowList(newGenreRowList);
  };
  // ジャンル登録
  const onSubmit = async (data: VideoCategorys) => {
    setIsLoad(true);
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        const displayOrder = videoCategorys.displayOrder !== '' && videoCategorys.displayOrder !== undefined
          ? videoCategorys.displayOrder
          : (rowList.length+1).toString();
        const value: VideoCategorys = {
          id: data?.id ?? '',
          title: data.title,
          type: "municipality",
          createUser: currentUser?.email ?? '',
          createAt: data?.createAt ?? new Date(),
          displayOrder: displayOrder,
        }

        if(isUpdate) {
          await companiesQuery.updateVideoCategories(currentUser?.tenantId!, value);
        } else {
          data.createUser = currentUser?.uid!;
          data.createAt = new Date();
          data.id = await companiesQuery.setVideoCategories(currentUser?.tenantId!, value);
        }
        const contentsMap = new Map();
        await Promise.all(videoContentsList.map(async (contents) => {
          if (!contents.categoryIdOrderMaps) {
            contents.categoryIdOrderMaps = {};
          }
          // 一旦コンテンツのcategoryIdOrderMapsからdata.idを削除
          delete contents.categoryIdOrderMaps[data.id];
          await companiesQuery.updateVideoContents(currentUser?.tenantId!, contents);
          // Mapにset
          return contentsMap.set(
            contents.id, {
              id: contents.id,
              type: contents.type,
              url: contents.url,
              title: contents.title,
              explanation: contents.explanation,
              categoryIdOrderMaps: contents.categoryIdOrderMaps,
              createUser: contents.createUser,
              createUserType: contents.createUserType,
              createAt: contents.createAt,
              pointSettingFlg: contents.pointSettingFlg,
              pointId: contents.pointId,
              pointTypeId: contents.pointTypeId,
              pointTypeName: contents.pointTypeName,
              pointName: contents.pointName,
              pointAssignmentPoint: contents.pointAssignmentPoint,
              pointCategoryId: contents.pointCategoryId,
              pointCategoryName: contents.pointCategoryName,
            }
          )
        }));
        // ジャンルで選択したコンテンツ更新・登録
        await Promise.all(
          genreRowList.map(async (genreData: any, index: number): Promise<VideoContents> => {
            const rowData = genreData.rowData;
            const updateGenre = contentsMap.get(rowData.id)
            updateGenre.categoryIdOrderMaps[data.id] = (index + 1).toString();
            const videoContents = {
              id: updateGenre.id,
              categoryIdOrderMaps: updateGenre.categoryIdOrderMaps,
              type: updateGenre.type,
              title: updateGenre.title,
              explanation: updateGenre.explanation,
              url: updateGenre.url,
              createUser: updateGenre.createUser,
              createUserType: updateGenre.createUserType,
              createAt: updateGenre.createAt,
              pointSettingFlg: updateGenre.pointSettingFlg,
              pointId: updateGenre.pointId,
              pointTypeId: updateGenre.pointTypeId,
              pointTypeName: updateGenre.pointTypeName,
              pointName: updateGenre.pointName,
              pointAssignmentPoint: updateGenre.pointAssignmentPoint,
              pointCategoryId: updateGenre.pointCategoryId,
              pointCategoryName: updateGenre.pointCategoryName,
            };
            await companiesQuery.updateVideoContents(currentUser?.tenantId!, videoContents);
            return videoContents;
          })
        );
        dispatch(alertOpen(AlertValue("ジャンルを保存しました", "success")));
        toggle();
        isDisabled.current = false;
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        setIsLoad(false);
        isDisabled.current = false;
      }
    }
    setSubmitFlg(!submitFlg);
  };
  // ジャンル登録・編集モーダル
  const onEditGenre = async (videoCategorys: VideoCategorys,
    videoContentsList: VideoContents[]) => {
    setIsLoad(true);
    setIsUpdate(videoCategorys.id !== '');
    const videoContentsByGenresList = videoContentsList.filter((contents) =>
      contents.categoryIdOrderMaps &&
      contents.categoryIdOrderMaps[videoCategorys.id] !== undefined
    );
    const genreRowList: DraggableGridRow[] = videoContentsByGenresList.map(
      (videoContents, index) => {
        const number = videoContents.categoryIdOrderMaps[videoCategorys.id] as unknown;
        return {
          rowData: {
            id: videoContents.id,
            number: number as number,
            title: videoContents.title,
            type: videoContents.type,
            author: getRegisterName(videoContents.createUserType!, videoContents.createUser, staffList),
            createAt: convertDateToString(videoContents.createAt),
          }
        };
      });
    genreRowList.sort((a, b) => Number(a.rowData.number!) > Number(b.rowData.number!) ? 1 : -1)
    setGenreRowList(genreRowList);

    const selectContentsIdList = genreRowList.map((genre): string => genre.rowData.id.toString());
    setSelectContentsIdList(selectContentsIdList);
    setContentsSelectDialogSelectedIdList(selectContentsIdList);

    setVideoCategorys(videoCategorys);
    toggle();
    setIsLoad(false);
  };

  // コンテンツ選択モーダル開閉
  const selectContentsToggle = () => {
    setContentsSelectDialogSelectedIdList([...new Set(selectContentsIdList)]);
    setIsSelectContentsOpen(!isSelectContentsOpen);
  };
  // コンテンツ選択
  const onSelectContents = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentId = event.currentTarget.getAttribute("data-id") ?? '';
    if(event.target.checked){
      contentsSelectDialogSelectedIdList?.push(currentId);
      setContentsSelectDialogSelectedIdList([...new Set(contentsSelectDialogSelectedIdList)]);
    } else {
      const filter = contentsSelectDialogSelectedIdList?.filter((id) => id !== currentId)
      setContentsSelectDialogSelectedIdList([...new Set(filter)]);
    };
  };
  // コンテンツ選択確定
  const onSelectedContentsSubmit = () => {
    setIsLoad(true);
    const contentsMap = new Map();
    videoContentsList.map((contents) => {
      return contentsMap.set(contents.id,
        {
          'id': contents.id,
          'number': 0,
          'title': contents.title,
          'type': contents.type,
          'author': contents.createUser,
          'createAt': contents.createAt,
        }
      )
    });
    const newGenreRowList = contentsSelectDialogSelectedIdList?.map((id, index) => {
      const videoContents = contentsMap.get(id);
      return {
        rowData: {
          id: videoContents.id,
          number: index + 1,
          title: videoContents.title,
          type: videoContents.type,
          author: videoContents.author,
          createAt: convertDateToString(videoContents.createAt),
        }
      };
    }) ?? [];
    setGenreRowList(newGenreRowList);
    const selectContentsIdList = newGenreRowList.map((genre): string => genre.rowData.id.toString());
    setSelectContentsIdList([...new Set(selectContentsIdList)]);
    setContentsSelectDialogSelectedIdList([...new Set(selectContentsIdList)]);
    selectContentsToggle();
    setSubmitFlg(!submitFlg);
  };

  // 動画コンテンツ一覧モーダル開閉
  const contentsToggle = () => {
    setIsContentsOpen(!isContentsOpen);
  }
  // 動画コンテンツ登録・編集モーダル開閉
  const onContentsEdit = (videoContents: VideoContents) => {
    contentsAddToggle();
    setContentsData(videoContents);
  }
  const onContentsAdd = () => {
    contentsAddToggle();
    setContentsData(undefined);
  }
  const contentsAddToggle = () => {
    setIsContentsAddOpen(!isContentsAddOpen);
  }
  // 動画コンテンツ登録
  const onContentsAddSubmit = async (data: VideoContents) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        setIsLoad(true);
        setContentsData(data);
        if(data.id !== '' && data.id !== undefined){
          await companiesQuery.updateVideoContents(currentUser?.tenantId!, data);
          dispatch(alertOpen(AlertValue("保存しました。", "success")));
          contentsAddToggle();
          isDisabled.current = false;
          setSubmitFlg(!submitFlg);
          return;
        };
        data.createAt = new Date();
        data.createUser = currentUser?.email ?? '';
        data.createUserType = 'municipality';
        await companiesQuery.setVideoContents(currentUser?.tenantId!, data);
        dispatch(alertOpen(AlertValue("保存しました。", "success")));
        contentsAddToggle();
        isDisabled.current = false;
      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue("保存に失敗しました。", "danger")));
        isDisabled.current = false;
      }
      setSubmitFlg(!submitFlg);
    }
  }
  // 動画コンテンツ削除ダイアログ開閉
  const deleteContentsDialogToggle = () => {
    setIsContentsDialogOpen(!isContentsDialogOpen);
  };
  // 動画コンテンツ削除
  const onContentsDelete = async (videoContentsId: string) => {
    setIsLoad(true);
    await companiesQuery.deleteVideoContents(currentUser?.tenantId!, videoContentsId);
    dispatch(alertOpen(AlertValue("動画コンテンツを削除しました。", "success")));
    deleteContentsDialogToggle();
    setSubmitFlg(!submitFlg);
  };

  return (
    <div className="h-full">
      {/* ジャンル登録編集モーダル */}
      {isOpen && (<MovieCategoriesModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        videoCategory={videoCategorys}
        authorUid={currentUser?.uid!}
        isContentsOpen={isSelectContentsOpen}
        genreColumns={genreColumns}
        genreRowList={genreRowList}
        genreDraggable={genreDraggable}
        contentsToggle={selectContentsToggle}
        onSubmit={onSubmit}
        isLoad={isLoad}
      />)}
      {/* コンテンツ選択モーダル */}
      {isSelectContentsOpen && (<MovieContentsSelectModal
        children={undefined}
        open={isSelectContentsOpen}
        toggle={selectContentsToggle}
        closeOnClickOutside={false}
        contentsColumns={contentsColumns}
        contentsRowList={contentsRowList}
        onContentsSelect={onSelectedContentsSubmit}
        isLoad={isLoad}
      />)}
      {/* 動画コンテンツ一覧モーダル */}
      {isContentsOpen && (<MovieContentsModal
        children={undefined}
        open={isContentsOpen}
        toggle={contentsToggle}
        closeOnClickOutside={false}
        videoContentsCols={contentsListColumns}
        videoContentsRows={contentsRowList}
        onContentsAdd={onContentsAdd}
        isLoad={isLoad}
      />)}
      {/* 動画コンテンツ登録・編集モーダル */}
      {isContentsAddOpen && (<MovieContentsAddModal
        children={undefined}
        open={isContentsAddOpen}
        toggle={contentsAddToggle}
        closeOnClickOutside={false}
        data={contentsData}
        onContentsAddSubmit={onContentsAddSubmit}
        isLoad={isLoad}
      />)}
      {/* 動画コンテンツ削除確認モーダル */}
      {isContentsDialogOpen && (<MovieContentsDeleteModal
        children={undefined}
        open={isContentsDialogOpen}
        toggle={deleteContentsDialogToggle}
        closeOnClickOutside={false}
        videoContents={deleteVideoContents}
        handle={onContentsDelete}
        isLoad={isLoad}
      />)}
      {/* ジャンル削除確認モーダル */}
      {isDialogOpen && (<MovieCategoriesDeleteModal
        children={undefined}
        open={isDialogOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        videoCategorys={deleteVideoCategorys}
        handle={onDelete}
        isLoad={isLoad}
      />)}
      <div className="sm:flex p-6 sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl font-semibold text-gray-900">
            動画メニュー設定
          </h2>
        </div>
        <Button
          size={"sm"}
          className={"mr-2"}
          color={"violet"}
          onClick={contentsToggle}
          children={"動画コンテンツ一覧"}
        />
        <Button
          size={"sm"}
          onClick={() => {
            onEditGenre(newVideoCategorys, videoContentsList);
          }}
          children={"ジャンル新規作成"}
        />
      </div>
      {isLoad ? (<Spiner/>) : (
        <div className={"px-6"}>
          <DraggableTable
            gridCol={columns}
            gridRow={rowList}
            dragEndHandle={draggable}
          />
        </div>
      )}
    </div>);
}